import React from "react";
import StatCard from "@/components/common/StatCard";
import { DollarSign, ImageIcon, MonitorPlay, Percent, TrendingUp } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import DashboardActiveAds from "@/components/dashboard/DashboardActiveAds";
import DashboardScreens from "@/components/dashboard/DashboardScreens";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const DashboardSellerDashboard = () => {
  const { user } = useAuth();
  const { ads, screens, currentMonthSales, getSellerStats } = useData();

  // Obtener el perfil del usuario para obtener su porcentaje de comisión
  const { data: userProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  if (!user) {
    return null;
  }

  // Calculate global average occupancy rate
  const totalOccupancyRate = screens.reduce((acc, screen) => acc + screen.occupancyRate, 0);
  const averageOccupancy = screens.length > 0 ? Math.round(totalOccupancyRate / screens.length) : 0;

  // Filtrar solo los anuncios del vendedor actual para las estadísticas
  const sellerAds = ads.filter(ad => ad.sellerId === user.id);

  // Calcular ventas totales solo de este vendedor y del mes actual
  const sellerCurrentMonthSales = currentMonthSales.filter(sale => sale.sellerId === user.id);
  const totalCurrentMonthSales = sellerCurrentMonthSales.reduce((acc, sale) => acc + sale.amount, 0);

  // Obtener estadísticas calculadas para este vendedor
  const stats = getSellerStats(user.id);

  // Formatear la cifra de ventas
  const formattedTotalSales = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalCurrentMonthSales);

  // Calcular ganancias por comisión usando los porcentajes dinámicos de cada cliente
  const totalCommissionEarnings = sellerCurrentMonthSales.reduce((sum, sale) => {
    const saleCommission = (sale.amount * (sale.commissionPercentage || 0)) / 100;
    return sum + saleCommission;
  }, 0);

  const formattedCommissionEarnings = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalCommissionEarnings);

  // Obtener las pantallas donde este vendedor tiene anuncios
  const sellerScreenIds = Array.from(new Set(sellerAds.flatMap(ad => ad.screens.map(s => s.screenId))));
  const sellerScreensCount = sellerScreenIds.length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <StatCard
          title="Ventas del mes actual"
          value={formattedTotalSales}
          icon={<DollarSign size={24} className="text-green-600" />}
        />
        <StatCard
          title="Mis Ganancias"
          value={formattedCommissionEarnings}
          icon={<TrendingUp size={24} className="text-emerald-600" />}
        />
        <StatCard
          title="Pantallas con tus anuncios"
          value={sellerScreensCount}
          icon={<MonitorPlay size={24} className="text-outdoor-700" />}
        />
        <StatCard
          title="Tus anuncios activos"
          value={sellerAds.length}
          icon={<ImageIcon size={24} className="text-green-500" />}
        />
        <StatCard
          title="Ocupación promedio"
          value={`${averageOccupancy}%`}
          icon={<Percent size={24} className="text-amber-500" />}
        />
      </div>

      {/* Mostrar todas las pantallas disponibles */}
      <DashboardScreens screens={screens} isLoading={false} />

      {/* Mostrar solo los anuncios de este vendedor */}
      <DashboardActiveAds ads={sellerAds} isLoading={false} title="Tus anuncios activos" />
    </div>
  );
};

export default DashboardSellerDashboard;
